import ctl from '@netlify/classnames-template-literals'
import { useAtom, useAtomValue } from 'jotai'
import { useTranslation } from 'react-i18next'
import { LiveField, RichLiveField, StatefulImage } from '../rapid-ui'
import { chooseBrandControlColor, getContrastingTextColor } from '../utilities'
import { ColorProvider, LANDING_PAGE_BANNER_ASPECT_RATIO } from '../views'
import { AiGeneratedLabelBadge, useIsAiGeneratedImage } from './ai-generated-label'
import { useLogoUpload } from './hooks/useLogoUpload'
import { CampaignImage } from './images'
import {
	activeLandingImageToolAtom,
	campaignDetailsBrandingAtom,
	landingPageAtom,
	landingPageHasGeneratedContentAtom,
	landingPageMetaForceAiGeneratedLabelAtom,
	landingPageMetaLogoBackgroundFillColorAtom,
	landingPageMetaShowLogoAtom,
	landingPageStatusAtom,
} from './state'
import { trackLandingPageEdited } from './tracking'

const fallbackPalette = {
	foreground: 'rgba(255,255,255,1)',
	background: 'rgba(0,0,0,1)',
	accentForeground: 'rgba(255,255,255,1)',
	accentBackground: 'rgba(0,0,0,1)',
}

interface LandingPageSimulatorProps {}

export function LandingPageSimulator({}: LandingPageSimulatorProps) {
	const { t } = useTranslation('editor')
	const activeBranding = useAtomValue(campaignDetailsBrandingAtom)
	const landingPageStatus = useAtomValue(landingPageStatusAtom)
	const hasGeneratedContent = useAtomValue(landingPageHasGeneratedContentAtom)
	const isImageToolExecuting = useAtomValue(activeLandingImageToolAtom)
	const logoBackgroundFillColor = useAtomValue(landingPageMetaLogoBackgroundFillColorAtom)
	const showLogo = useAtomValue(landingPageMetaShowLogoAtom)
	const { triggerUpload, isUploading, fileInput } = useLogoUpload()

	const isGeneratingText = landingPageStatus === 'generating' && !hasGeneratedContent
	// Text should flash during generation or text tool execution (not image tools)
	const shouldFlashContent = ((landingPageStatus === 'generating' && hasGeneratedContent)
		|| landingPageStatus === 'tool-executing') && !isImageToolExecuting
	// Image should flash during image tool execution
	const shouldFlashImage = isImageToolExecuting
	const [landingPage, setLandingPage] = useAtom(landingPageAtom)
	const isImageAiGenerated = useIsAiGeneratedImage(landingPage?.imageId)
	const forceAiGeneratedLabel = useAtomValue(landingPageMetaForceAiGeneratedLabelAtom)

	const brandingPalette = {
		foreground: activeBranding?.foreground || fallbackPalette.foreground,
		background: activeBranding?.background || fallbackPalette.background,
		accentForeground: activeBranding?.accentForeground || fallbackPalette.accentForeground,
		accentBackground: activeBranding?.accentBackground || fallbackPalette.accentBackground,
	}

	// Mirrors form-viewer behaviour (ENG-4824): the start button uses the brand
	// background as a solid fill, which disappears on the white preview canvas
	// when the user picks a near-white brand color. Fall back to the foreground
	// in that case. Raw colors are still exposed via ColorProvider for the
	// rich-text color picker.
	const brandControlColor = chooseBrandControlColor(brandingPalette.background, brandingPalette.foreground)

	if (!landingPage) {
		return null
	}

	return (
		<ColorProvider brandingPalette={brandingPalette}>
			<div
				style={{
					width: '500px',
					fontFamily: 'Onest',
					'--logo-background-fill-color': logoBackgroundFillColor ?? '#ffffff',
				} as React.CSSProperties}
				className="relative ring perspective-dramatic ring-grey-200 bg-white flex flex-col rounded-sm shadow-lg"
			>
				<div className="flex flex-col gap-4 pb-14">
					<div className="flex items-center gap-2">
						{(showLogo ?? true) && (
							<div
								className="absolute top-0 z-10 left-8 px-2 pt-3 pb-2 rounded-b-sm"
								style={{
									backgroundColor: 'var(--logo-background-fill-color)',
								}}
							>
								<div className=" size-12">
									<StatefulImage
										src={activeBranding?.logo?.url ?? ''}
										alt={t('simulator.landingPage.logoAlt')}
										className="h-12 w-12 cursor-pointer"
										style={{ backgroundColor: 'transparent' }}
										objectFit="contain"
										onClick={triggerUpload}
										forceIsLoading={isUploading}
									/>
								</div>
							</div>
						)}
						<div className="relative w-full">
							<CampaignImage
								imageId={landingPage.imageId}
								className={ctl(`
									w-full aspect-[16/9] rounded-t-sm
									${shouldFlashImage ? 'animate-pulse' : ''}
								`)}
								color={brandingPalette.background}
								crop={{
									imageScale: landingPage.meta.imageScale,
									imageOffsetX: landingPage.meta.imageOffsetX,
									imageOffsetY: landingPage.meta.imageOffsetY,
								}}
								frameAspectRatio={LANDING_PAGE_BANNER_ASPECT_RATIO}
							/>
							{(isImageAiGenerated || forceAiGeneratedLabel) && (
								<div className="absolute right-3 bottom-3 w-[22%] z-10">
									<AiGeneratedLabelBadge />
								</div>
							)}
						</div>
					</div>
					<div
						className={ctl(`
						flex flex-col gap-2 px-6
						${shouldFlashContent ? 'animate-pulse' : ''}
					`)}
					>
						<div className="text-4xl font-semibold mt-12 mb-4">
							<LiveField
								isGenerating={isGeneratingText}
								value={landingPage.title}
								onChange={(title) => {
									setLandingPage({ ...landingPage, title })
									trackLandingPageEdited('title')
								}}
							/>
						</div>
						<div className="text-lg mb-10">
							<LiveField
								isGenerating={isGeneratingText}
								value={landingPage.subtitle}
								onChange={(subtitle) => {
									setLandingPage({ ...landingPage, subtitle })
									trackLandingPageEdited('subtitle')
								}}
							/>
						</div>
						<RichLiveField
							isGenerating={isGeneratingText}
							value={landingPage.body}
							onChange={(body) => {
								if (isGeneratingText) { return }
								setLandingPage({ ...landingPage, body })
								trackLandingPageEdited('body')
							}}
						/>
						<div className="mt-12">
							<button
								className="px-4 py-2 rounded-sm"
								style={{
									backgroundColor: brandControlColor,
									color: getContrastingTextColor(brandControlColor),
								}}
							>
								{t('simulator.landingPage.startButton')}
							</button>
						</div>
					</div>
				</div>
				{fileInput}
			</div>
		</ColorProvider>
	)
}
